import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import Chip from '@mui/material/Chip';
import { Translate, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { TourKind } from 'app/shared/model/enumerations/tour-kind.model';
import { TourMode } from 'app/shared/model/enumerations/tour-mode.model';
import { DurationMeasure } from 'app/shared/model/enumerations/duration-measure.model';
import { getEntity } from './tour.reducer';

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
    {children}
  </Typography>
);

const FieldValue = ({ children, sx }: { children: React.ReactNode; sx?: object }) => (
  <Typography variant="body1" sx={{ mb: 2, ...sx }}>
    {children || '-'}
  </Typography>
);

const BooleanDisplay = ({ value }: { value?: boolean }) =>
  value ? (
    <Chip icon={<FontAwesomeIcon icon="check" />} label="Yes" color="success" size="small" />
  ) : (
    <Chip icon={<FontAwesomeIcon icon="times" />} label="No" color="error" size="small" />
  );

const translateEnum = (value: string, enumKey: string) => {
  return translate(`jhipsterSampleApplicationApp.${enumKey}.${value}`) || value;
};

const TourDetail = () => {
  const dispatch = useAppDispatch();
  const { id } = useParams<'id'>();

  useEffect(() => {
    dispatch(getEntity(id));
  }, []);

  const tourEntity = useAppSelector(state => state.tour.entity);
  const tourKindValues = Object.keys(TourKind);
  const tourModeValues = Object.keys(TourMode);
  const durationMeasureValues = Object.keys(DurationMeasure);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <h2 id="jhipsterSampleApplicationApp.tour.detail.title" data-cy="TourDetailHeading">
          <Translate contentKey="jhipsterSampleApplicationApp.tour.detail.title">Tour Details</Translate>
        </h2>
        {tourEntity.id && (
          <Box sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', px: 2, py: 0.5, borderRadius: 1, fontWeight: 'bold' }}>
            ID: {tourEntity.id}
          </Box>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 8 }}>
              <FieldLabel>
                <Translate contentKey="jhipsterSampleApplicationApp.tour.code" />
              </FieldLabel>
              <FieldValue>{tourEntity.code}</FieldValue>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <FieldLabel>
                <Translate contentKey="jhipsterSampleApplicationApp.tour.enabled" />
              </FieldLabel>
              <BooleanDisplay value={tourEntity.enabled} />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }}>
              <FieldLabel>
                <Translate contentKey="jhipsterSampleApplicationApp.tour.kind" />
              </FieldLabel>
              <FieldValue>{translateEnum(tourEntity.kind, 'TourKind')}</FieldValue>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FieldLabel>
                <Translate contentKey="jhipsterSampleApplicationApp.tour.mode" />
              </FieldLabel>
              <FieldValue>{translateEnum(tourEntity.mode, 'TourMode')}</FieldValue>
            </Grid>
          </Grid>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.tour.icon" />
          </FieldLabel>
          <FieldValue>{tourEntity.icon}</FieldValue>

          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }}>
              <FieldLabel>
                <Translate contentKey="jhipsterSampleApplicationApp.tour.duration" />
              </FieldLabel>
              <FieldValue>{tourEntity.duration}</FieldValue>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FieldLabel>
                <Translate contentKey="jhipsterSampleApplicationApp.tour.durationMeasure" />
              </FieldLabel>
              <FieldValue>{translateEnum(tourEntity.durationMeasure, 'DurationMeasure')}</FieldValue>
            </Grid>
          </Grid>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.tour.badge" />
          </FieldLabel>
          <FieldValue>{tourEntity.badge}</FieldValue>

          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }}>
              <FieldLabel>
                <Translate contentKey="jhipsterSampleApplicationApp.tour.initialPrice" />
              </FieldLabel>
              <FieldValue>{tourEntity.initialPrice}</FieldValue>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FieldLabel>
                <Translate contentKey="jhipsterSampleApplicationApp.tour.price" />
              </FieldLabel>
              <FieldValue>{tourEntity.price}</FieldValue>
            </Grid>
          </Grid>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.tour.rating" />
          </FieldLabel>
          <FieldValue>{tourEntity.rating}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.tour.widgetId" />
          </FieldLabel>
          <FieldValue>{tourEntity.widgetId}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.tour.externalId" />
          </FieldLabel>
          <FieldValue>{tourEntity.externalId}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.tour.defaultImageData" />
          </FieldLabel>
          {tourEntity.defaultImageData ? (
            <Box sx={{ mb: 2 }}>
              <img
                src={`data:${tourEntity.defaultImageDataContentType};base64,${tourEntity.defaultImageData}`}
                alt="Default"
                style={{ maxHeight: '150px', borderRadius: '4px' }}
              />
            </Box>
          ) : (
            <FieldValue>No image</FieldValue>
          )}

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.tour.cssStyle" />
          </FieldLabel>
          <FieldValue sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.875rem' }}>{tourEntity.cssStyle}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.tour.destination" />
          </FieldLabel>
          <FieldValue>{tourEntity.destination?.code}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.tour.defaultCategory" />
          </FieldLabel>
          <FieldValue>{tourEntity.defaultCategory?.code}</FieldValue>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Options
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <FormControlLabel
                  control={<Switch checked={tourEntity.petFriendly || false} disabled />}
                  label={translate('jhipsterSampleApplicationApp.tour.petFriendly')}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <FormControlLabel
                  control={<Switch checked={tourEntity.kidsAllowed || false} disabled />}
                  label={translate('jhipsterSampleApplicationApp.tour.kidsAllowed')}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <FormControlLabel
                  control={<Switch checked={tourEntity.smoking || false} disabled />}
                  label={translate('jhipsterSampleApplicationApp.tour.smoking')}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <FormControlLabel
                  control={<Switch checked={tourEntity.app || false} disabled />}
                  label={translate('jhipsterSampleApplicationApp.tour.app')}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <FormControlLabel
                  control={<Switch checked={tourEntity.accessibility || false} disabled />}
                  label={translate('jhipsterSampleApplicationApp.tour.accessibility')}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <FormControlLabel
                  control={<Switch checked={tourEntity.audioGuide || false} disabled />}
                  label={translate('jhipsterSampleApplicationApp.tour.audioGuide')}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <FormControlLabel
                  control={<Switch checked={tourEntity.tourGuide || false} disabled />}
                  label={translate('jhipsterSampleApplicationApp.tour.tourGuide')}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <FormControlLabel
                  control={<Switch checked={tourEntity.displayOnHomepage || false} disabled />}
                  label={translate('jhipsterSampleApplicationApp.tour.displayOnHomepage')}
                />
              </Grid>
            </Grid>
          </Paper>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.tour.availableFromDate" />
          </FieldLabel>
          <FieldValue>{tourEntity.availableFromDate ? new Date(tourEntity.availableFromDate).toLocaleDateString() : '-'}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.tour.availableToDate" />
          </FieldLabel>
          <FieldValue>{tourEntity.availableToDate ? new Date(tourEntity.availableToDate).toLocaleDateString() : '-'}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.tour.createdDate" />
          </FieldLabel>
          <FieldValue>{tourEntity.createdDate ? new Date(tourEntity.createdDate).toLocaleDateString() : '-'}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.tour.departure" />
          </FieldLabel>
          <FieldValue>{tourEntity.departure || '-'}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.tour.returnTime" />
          </FieldLabel>
          <FieldValue>{tourEntity.returnTime || '-'}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.tour.content" />
          </FieldLabel>
          <FieldValue>{tourEntity.content?.code}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.tour.createdBy" />
          </FieldLabel>
          <FieldValue>{tourEntity.createdBy?.login}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.tour.meetingPoint" />
          </FieldLabel>
          <FieldValue>{tourEntity.meetingPoint?.code}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.tour.finishPoint" />
          </FieldLabel>
          <FieldValue>{tourEntity.finishPoint?.code}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.tour.tourExtra" />
          </FieldLabel>
          <FieldValue>{tourEntity.tourExtras?.map((t: any) => t.code).join(', ') || '-'}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.tour.tags" />
          </FieldLabel>
          <FieldValue>{tourEntity.tags?.map((t: any) => t.code).join(', ') || '-'}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.tour.promotions" />
          </FieldLabel>
          <FieldValue>{tourEntity.promotions?.map((p: any) => p.code).join(', ') || '-'}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.tour.category" />
          </FieldLabel>
          <FieldValue>{tourEntity.categories?.map((c: any) => c.code).join(', ') || '-'}</FieldValue>
        </Grid>

        <Grid size={12}>
          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button component={Link} to="/tour" replace color="info" variant="outlined" startIcon={<FontAwesomeIcon icon="arrow-left" />}>
              <Translate contentKey="entity.action.back">Back</Translate>
            </Button>
            <Button
              component={Link}
              to={`/tour/${tourEntity.id}/edit`}
              replace
              color="primary"
              variant="contained"
              startIcon={<FontAwesomeIcon icon="pencil-alt" />}
            >
              <Translate contentKey="entity.action.edit">Edit</Translate>
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TourDetail;
